import React, {
  lazy,
  Suspense,
  useState,
  useEffect,
  useCallback,
  useRef,
  useTransition,
} from "react";
import { useNavigate } from "react-router-dom";

// Auth check
import { tokenManager } from "@/shared/services/tokenManager";

// Shell components - loaded immediately (lightweight)
import { HeroShell } from "@/marketing/components/full-site/HeroShell";
import { HeroShellLoading } from "@/marketing/components/full-site/HeroShellLoading";
import { ChunkErrorBoundary } from "@/marketing/components/full-site/ChunkErrorBoundary";
// recordNavigation is dynamic-imported on first use below so this above-fold
// eager component does not pull rrweb (~40KB brotli) into the main bundle.
// This was the sole eager import chain leaking rrweb into index.js.

// CRITICAL: lazy() MUST be at module level - NOT inside component
// Declaring inside component causes remounting on every render
const HeroSectionImmersive = lazy(() => import("@/marketing/components/full-site/HeroSectionImmersive"));
const HeroSectionImmersiveMobile = lazy(
  () => import("@/marketing/components/full-site/HeroSectionImmersive.mobile")
);
// AppShell provides Redux + WebSocketProvider + NotificationProvider —
// needed because HeroSectionImmersive mounts PlatformLayout which uses
// useSelector. The landing route is OUTSIDE AppShell by design (to keep
// Redux off the marketing critical path), so we wrap the demo activation
// in AppShell at the moment it mounts.
const AppShell = lazy(() => import("@/AppShell"));

const MOBILE_BREAKPOINT = 768;

/**
 * Responsive wrapper implementing shell-first lazy loading architecture.
 *
 * Performance pattern:
 * 1. Renders HeroShell immediately (lightweight ~30KB)
 * 2. Lazy loads HeroSectionImmersive (~400KB+) only when user interacts
 * 3. Background preloads heavy component after 5s for faster transition
 *
 * This ensures:
 * - Fast FCP/LCP (shell renders instantly)
 * - No blank page on load
 * - Smooth transition via useTransition
 * - Error recovery via ChunkErrorBoundary
 *
 * @see .planning/website_optimisation/WEBSITE_LOADING_OPTIMIZATION.md
 */
const HeroSectionResponsive: React.FC = () => {
  const navigate = useNavigate();

  // Activation state - when true, renders heavy component
  const [isActivated, setIsActivated] = useState(false);

  // useTransition for smooth loading without blocking UI
  const [, startTransition] = useTransition();

  // Track if preload already triggered
  const preloadTriggered = useRef(false);

  // Browser back button support - reset to landing when user presses back
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // If we're in activated state and user pressed back, return to landing
      if (event.state?.heroActivated === false || !event.state?.heroActivated) {
        setIsActivated(false);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);


  // Mobile detection state
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches;
  });

  // Media query listener for responsive switching
  useEffect(() => {
    const mediaQuery = window.matchMedia(
      `(max-width: ${MOBILE_BREAKPOINT - 1}px)`
    );

    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    // Safari < 14 fallback
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  // Preload function - triggers dynamic import without rendering
  const preloadHeavyComponent = useCallback(() => {
    if (preloadTriggered.current) return;
    preloadTriggered.current = true;

    // Only preload the variant we need based on screen size
    if (window.innerWidth < MOBILE_BREAKPOINT) {
      import("@/marketing/components/full-site/HeroSectionImmersive.mobile");
    } else {
      import("@/marketing/components/full-site/HeroSectionImmersive");
    }
  }, []);

  // Background preload after page fully loaded + 5s delay
  // This ensures TBT is not affected during initial page load
  useEffect(() => {
    const handleLoad = () => {
      setTimeout(preloadHeavyComponent, 5000);
    };

    if (document.readyState === "complete") {
      setTimeout(preloadHeavyComponent, 5000);
    } else {
      window.addEventListener("load", handleLoad);
      return () => window.removeEventListener("load", handleLoad);
    }
  }, [preloadHeavyComponent]);

  // Activation handler - called when user interacts with omnibar
  const handleActivate = useCallback((initialValue?: string) => {
    // Check if user is logged in - redirect to /platform if so
    const token = tokenManager.getAccessToken();
    const isLoggedIn = token && !tokenManager.isTokenExpired();

    if (isLoggedIn) {
      // User is authenticated - redirect to the real platform
      navigate('/platform');
      return;
    }

    // Not logged in - activate simulation environment
    // Push history state so browser back returns to landing
    window.history.pushState({ heroActivated: true }, "", window.location.href);

    // Record SIM activation for session replay. Dynamic import keeps rrweb
    // out of the eager bundle — fired on user interaction, not initial render.
    import('@/marketing/utils/sessionRecorder').then((m) => m.recordNavigation('SIM_ACTIVATED'));

    // Use startTransition for smooth loading without blocking
    startTransition(() => {
      setIsActivated(true);
    });
  }, [navigate]);

  // Track previous activation state to detect return from demo
  const wasActivatedRef = useRef(false);
  const activationCountRef = useRef(0);

  // Only increment when transitioning from activated → non-activated (returning from demo)
  // This prevents HeroShell from remounting on every parent re-render
  useEffect(() => {
    if (wasActivatedRef.current && !isActivated) {
      activationCountRef.current++;
    }
    wasActivatedRef.current = isActivated;
  }, [isActivated]);

  // Before activation: render lightweight shell
  // Key ensures AnimatedHeaderMorph remounts fresh after returning from demo
  if (!isActivated) {
    return <HeroShell key={`shell-${activationCountRef.current}`} onActivate={handleActivate} />;
  }

  // After activation: render full platform with error boundary + suspense
  // Wrapped in <AppShell> because HeroSectionImmersive → PlatformLayout
  // uses Redux (useSelector). Pass initiallyActive={true} to skip the
  // landing state since user already activated from shell.
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<HeroShellLoading />}>
        <AppShell>
          {isMobile ? (
            <HeroSectionImmersiveMobile initiallyActive={true} />
          ) : (
            <HeroSectionImmersive initiallyActive={true} />
          )}
        </AppShell>
      </Suspense>
    </ChunkErrorBoundary>
  );
};

export default HeroSectionResponsive;
